/**
 * App Component
 *
 * Main application entry point with React Router configuration.
 * All pages are lazy loaded for optimal code splitting.
 * Wrapped in AuthProvider to supply auth state to the entire app.
 */

import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { OutletLayout } from '@/layouts/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ScrollToHash } from '@/components/shared/ScrollToHash';

// Lazy load pages for code splitting
// Core Pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const AiHelpPage = lazy(() => import('@/pages/AiHelpPage'));

// Cosmos Pages (Feature 010 - Consolidated Landing)
const CosmosMainPage = lazy(() => import('@/pages/cosmos/CosmosMainPage'));
const CosmosLandingPage = lazy(() => import('@/pages/cosmos/CosmosLandingPage'));
const CosmosPricingPage = lazy(() => import('@/pages/cosmos/CosmosPricingPage'));
const CosmosStatusPage = lazy(() => import('@/pages/cosmos/CosmosStatusPage'));
const CosmosThankYouPage = lazy(() => import('@/pages/cosmos/CosmosThankYouPage'));
const CosmosContactPage = lazy(() => import('@/pages/cosmos/CosmosContactPage'));
const CosmosRealtorPage = lazy(() => import('@/pages/cosmos/CosmosRealtorPage'));
const CosmosTechPage = lazy(() => import('@/pages/cosmos/CosmosTechPage'));
const CosmosResearchPage = lazy(() => import('@/pages/cosmos/CosmosResearchPage'));
const CosmosSalesPage = lazy(() => import('@/pages/cosmos/CosmosSalesPage'));
const CosmosMarketingPage = lazy(() => import('@/pages/cosmos/CosmosMarketingPage'));
const CosmosAcademicPage = lazy(() => import('@/pages/cosmos/CosmosAcademicPage'));
const CosmosBiotechPage = lazy(() => import('@/pages/cosmos/CosmosBiotechPage'));
const CosmosFoundersPage = lazy(() => import('@/pages/cosmos/CosmosFoundersPage'));
const CosmosProductRndPage = lazy(() => import('@/pages/cosmos/CosmosProductRndPage'));

// CosmosPrivacyPage and CosmosTermsPage removed — routes redirect to /privacy and /terms
const CosmosResetPasswordPage = lazy(() => import('@/pages/cosmos/CosmosResetPasswordPage'));
const CosmosRealtorPricingPage = lazy(() => import('@/pages/cosmos/CosmosRealtorPricingPage'));
const CosmosRealtorThankYouPage = lazy(() => import('@/pages/cosmos/CosmosRealtorThankYouPage'));
const CosmosRealtorContactPage = lazy(() => import('@/pages/cosmos/CosmosRealtorContactPage'));
const CosmosRealtorStatusPage = lazy(() => import('@/pages/cosmos/CosmosRealtorStatusPage'));
const CosmosLeaderboardPage = lazy(() => import('@/pages/cosmos/CosmosLeaderboardPage'));
const CosmosRealtorLeaderboardPage = lazy(() => import('@/pages/cosmos/CosmosRealtorLeaderboardPage'));

// Cosmos Auth Pages (Spec 014 Layer 4, Spec K - Cosmos Primary)
const CosmosLoginPage = lazy(() => import('@/pages/cosmos/CosmosLoginPage'));
const CosmosSignupPage = lazy(() => import('@/pages/cosmos/CosmosSignupPage'));
const CosmosAuthCallbackPage = lazy(() => import('@/pages/cosmos/CosmosAuthCallbackPage'));
const CosmosAccountPage = lazy(() => import('@/pages/cosmos/CosmosAccountPage'));
const CosmosAdminPage = lazy(() => import('@/pages/cosmos/CosmosAdminPage'));
const CosmosDownloadPage = lazy(() => import('@/pages/cosmos/CosmosDownloadPage'));
const CosmosCheckoutPage = lazy(() => import('@/pages/cosmos/CosmosCheckoutPage'));

// Cosmos Realtor Auth Pages (Spec 014 Layer 4, Spec L - Realtor)
const CosmosRealtorLoginPage = lazy(() => import('@/pages/cosmos/CosmosRealtorLoginPage'));
const CosmosRealtorSignupPage = lazy(() => import('@/pages/cosmos/CosmosRealtorSignupPage'));
const CosmosRealtorAuthCallbackPage = lazy(() => import('@/pages/cosmos/CosmosRealtorAuthCallbackPage'));
const CosmosRealtorAccountPage = lazy(() => import('@/pages/cosmos/CosmosRealtorAccountPage'));
const CosmosRealtorDownloadPage = lazy(() => import('@/pages/cosmos/CosmosRealtorDownloadPage'));

/**
 * Loading fallback component for Suspense
 */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary">
      <LoadingSpinner
        size="lg"
        label="Loading page..."
        showLabel
      />
    </div>
  );
}

/**
 * Skip link for keyboard accessibility
 * Allows keyboard users to skip navigation and go directly to main content
 */
function SkipLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-accent-amber focus:text-bg-primary focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-amber"
    >
      Skip to main content
    </a>
  );
}

/**
 * Detects Supabase recovery tokens in the URL and redirects to the
 * reset password page. Supports both PKCE flow (query params:
 * ?token_hash=...&type=recovery) and legacy hash fragment flow.
 */
function RecoveryRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    // PKCE flow: ?token_hash=...&type=recovery (current Supabase default)
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('type') === 'recovery' && searchParams.get('token_hash')) {
      navigate(`/cosmos/auth/reset-password${window.location.search}`, { replace: true });
      return;
    }

    // Legacy hash fragment flow: #access_token=...&type=recovery
    const hash = window.location.hash.substring(1);
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      if (hashParams.get('type') === 'recovery') {
        navigate(`/cosmos/auth/reset-password${window.location.search}${window.location.hash}`, { replace: true });
      }
    }
  }, [navigate]);

  return null;
}

/**
 * Main App Component
 *
 * Configures all application routes with lazy loading.
 * Wrapped in AuthProvider to supply Supabase auth state globally.
 *
 * Route Structure:
 * - /                                  - HomePage
 * - /about                             - AboutPage
 * - /projects                          - ProjectsPage (placeholder)
 * - /contact                           - ContactPage
 * - /privacy                           - PrivacyPage
 * - /terms                             - TermsPage
 * - /ai-help                           - AiHelpPage
 * - /cosmos                            - CosmosMainPage
 * - /cosmos/whycosmos                  - CosmosLandingPage
 * - /cosmos/pricing                    - CosmosPricingPage
 * - /cosmos/contact                    - CosmosContactPage
 * - /cosmos/tech                       - CosmosTechPage
 * - /cosmos/research                   - CosmosResearchPage
 * - /cosmos/sales                      - CosmosSalesPage
 * - /cosmos/marketing                  - CosmosMarketingPage
 * - /cosmos/academic                   - CosmosAcademicPage (ICP 1)
 * - /cosmos/biotech                    - CosmosBiotechPage (ICP 2)
 * - /cosmos/founders                   - CosmosFoundersPage (ICP 3)
 * - /cosmos/product-rnd                - CosmosProductRndPage (ICP 4)
 * - /cosmos/status                     - CosmosStatusPage
 * - /cosmos/thank-you                  - CosmosThankYouPage
 * - /cosmos/login                      - CosmosLoginPage
 * - /cosmos/signup                     - CosmosSignupPage
 * - /cosmos/auth/callback              - CosmosAuthCallbackPage
 * - /cosmos/account                    - CosmosAccountPage (protected)
 * - /cosmos/download                   - CosmosDownloadPage (protected)
 * - /cosmos/privacy                    - Redirects to /privacy
 * - /cosmos/terms                      - Redirects to /terms
 * - /cosmos/realtor                    - CosmosRealtorPage
 * - /cosmos/realtor/pricing            - CosmosRealtorPricingPage
 * - /cosmos/realtor/thank-you          - CosmosRealtorThankYouPage
 * - /cosmos/realtor/contact            - CosmosRealtorContactPage
 * - /cosmos/realtor/status             - CosmosRealtorStatusPage
 * - /cosmos/realtor/leaderboard        - CosmosRealtorLeaderboardPage
 * - /cosmos/realtor/login              - CosmosRealtorLoginPage
 * - /cosmos/realtor/signup             - CosmosRealtorSignupPage
 * - /cosmos/realtor/auth/callback      - CosmosRealtorAuthCallbackPage
 * - /cosmos/realtor/account            - CosmosRealtorAccountPage (protected)
 * - /cosmos/realtor/download           - CosmosRealtorDownloadPage (protected)
 * - *                                  - NotFoundPage (404)
 */
export default function App() {
  return (
    <AuthProvider>
      {/* Skip Link for Accessibility */}
      <SkipLink />

      {/* Redirect recovery tokens to reset password page */}
      <RecoveryRedirect />

      {/* Handle hash-based scrolling */}
      <ScrollToHash />

      {/* Routes wrapped in Suspense for lazy loading */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Core Pages — wrapped in Layout (Nav + Footer) */}
          <Route element={<OutletLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/ai-help" element={<AiHelpPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Cosmos Pages — own nav/footer, NO Layout wrapper */}
          <Route path="/cosmos" element={<CosmosMainPage />} />
          <Route path="/cosmos/whycosmos" element={<CosmosLandingPage />} />
          <Route path="/cosmos/pricing" element={<CosmosPricingPage />} />
          <Route path="/cosmos/contact" element={<CosmosContactPage />} />
          <Route path="/cosmos/status" element={<CosmosStatusPage />} />
          <Route path="/cosmos/thank-you" element={<CosmosThankYouPage />} />
          <Route path="/cosmos/tech" element={<CosmosTechPage />} />
          <Route path="/cosmos/research" element={<CosmosResearchPage />} />
          <Route path="/cosmos/sales" element={<CosmosSalesPage />} />

          <Route path="/cosmos/marketing" element={<CosmosMarketingPage />} />

          {/* Cosmos ICP verticals — landing destinations for the outbound
              lead-gen campaigns (ICP 1–4 in the outreach dossier). */}
          <Route path="/cosmos/academic" element={<CosmosAcademicPage />} />
          <Route path="/cosmos/biotech" element={<CosmosBiotechPage />} />
          <Route path="/cosmos/founders" element={<CosmosFoundersPage />} />
          <Route path="/cosmos/product-rnd" element={<CosmosProductRndPage />} />

          <Route path="/cosmos/leaderboard" element={<CosmosLeaderboardPage />} />

          {/* Cosmos Auth Pages (Spec K) */}
          <Route path="/cosmos/login" element={<CosmosLoginPage />} />
          <Route path="/cosmos/signup" element={<CosmosSignupPage />} />
          <Route path="/cosmos/auth/callback" element={<CosmosAuthCallbackPage />} />
          <Route path="/cosmos/auth/reset-password" element={<CosmosResetPasswordPage />} />
          <Route path="/cosmos/account" element={<CosmosAccountPage />} />
          <Route path="/cosmos/admin" element={<CosmosAdminPage />} />
          <Route path="/cosmos/checkout" element={<CosmosCheckoutPage />} />
          <Route path="/cosmos/download" element={<CosmosDownloadPage />} />

          {/* Cosmos Realtor Pages — own nav/footer */}
          <Route path="/cosmos/realtor" element={<CosmosRealtorPage />} />
          <Route path="/cosmos/realtor/pricing" element={<CosmosRealtorPricingPage />} />
          <Route path="/cosmos/realtor/thank-you" element={<CosmosRealtorThankYouPage />} />
          <Route path="/cosmos/realtor/contact" element={<CosmosRealtorContactPage />} />
          <Route path="/cosmos/realtor/status" element={<CosmosRealtorStatusPage />} />
          <Route path="/cosmos/realtor/leaderboard" element={<CosmosRealtorLeaderboardPage />} />

          {/* Cosmos Realtor Auth Pages (Spec L) */}
          <Route path="/cosmos/realtor/login" element={<CosmosRealtorLoginPage />} />
          <Route path="/cosmos/realtor/signup" element={<CosmosRealtorSignupPage />} />
          <Route path="/cosmos/realtor/auth/callback" element={<CosmosRealtorAuthCallbackPage />} />
          <Route path="/cosmos/realtor/account" element={<CosmosRealtorAccountPage />} />
          <Route path="/cosmos/realtor/download" element={<CosmosRealtorDownloadPage />} />

          {/* Cosmos Legal — redirect to main site legal pages */}
          <Route path="/cosmos/privacy" element={<Navigate to="/privacy" replace />} />
          <Route path="/cosmos/terms" element={<Navigate to="/terms" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
